import { useQueryClient } from "@tanstack/react-query";
import { useState, useTransition } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ApiError, post } from "@/lib/api/_client";
import { FEATURE_FLAG_METADATA, FEATURE_FLAGS } from "@/lib/feature-flags";

type TenantFeatureFlagsCardProps = {
  tenantId: string;
  featureFlags: {
    id: string;
    flagKey: string;
    enabled: boolean;
  }[];
  hidePartnerFlag?: boolean;
};

export function TenantFeatureFlagsCard({
  tenantId,
  featureFlags: initialFlags,
  hidePartnerFlag = false,
}: TenantFeatureFlagsCardProps) {
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [flags, setFlags] = useState(initialFlags);
  const queryClient = useQueryClient();

  const handleToggle = async (flagKey: string, currentlyEnabled: boolean) => {
    setError(null);

    startTransition(async () => {
      try {
        await post("/internal/admin/feature-flags/toggle", {
          tenantId,
          flagKey,
          enabled: !currentlyEnabled,
        });
        // Invalidate tenant list so partner column updates
        queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
        // Update local state
        setFlags((prev) => {
          const existing = prev.find((f) => f.flagKey === flagKey);
          if (existing) {
            return prev.map((f) =>
              f.flagKey === flagKey ? { ...f, enabled: !currentlyEnabled } : f,
            );
          }
          return [...prev, { id: flagKey, flagKey, enabled: !currentlyEnabled }];
        });
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message || "Failed to toggle feature flag"
            : "Failed to toggle feature flag",
        );
      }
    });
  };

  const isFeatureEnabled = (flagKey: string): boolean => {
    const flag = flags.find((f) => f.flagKey === flagKey);
    return flag?.enabled ?? false;
  };

  const allFeatureFlags = Object.values(FEATURE_FLAGS).filter(
    (flagKey) => !hidePartnerFlag || flagKey !== FEATURE_FLAGS.PARTNER,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature Flags</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}

        <div className="space-y-3">
          {allFeatureFlags.map((flagKey) => {
            const metadata = FEATURE_FLAG_METADATA[flagKey];
            const enabled = isFeatureEnabled(flagKey);

            return (
              <div
                key={flagKey}
                className="flex items-start justify-between space-x-4 border rounded-lg p-4"
              >
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={`flag-${flagKey}`}
                    className="text-base font-medium cursor-pointer"
                  >
                    {metadata.name}
                  </Label>
                  <p className="text-sm text-muted-foreground">{metadata.description}</p>
                  <p className="text-xs text-muted-foreground font-mono">{flagKey}</p>
                </div>
                <Switch
                  id={`flag-${flagKey}`}
                  checked={enabled}
                  onCheckedChange={() => handleToggle(flagKey, enabled)}
                  disabled={isPending}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
